import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { gaEvent } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Camping",
  "PRL (Parc Résidentiel de Loisirs)",
  "Domaine nature",
  "Hôtellerie de plein air",
  "Collectivité",
  "Hébergement saisonnier",
  "Projet en réflexion",
  "Autre",
];

export default function Contact() {
  useSEO({
    title: "Contact — Étudions votre projet d'hébergement",
    description:
      "Contactez YONO pour étudier votre projet d'hébergement bois : camping, PRL, domaine nature, hôtellerie de plein air, hébergement saisonnier. Réponse sous 48 heures.",
    path: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact YONO Habitat",
      url: "https://yono-habitat.com/contact",
    },
  });

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      gaEvent("contact_submit", {
        project_type: form.project_type || "non-renseigne",
        has_phone: Boolean(form.phone),
      });
      setDone(true);
      navigate("/contact/merci");
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez ou écrivez-nous directement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Toaster richColors position="top-right" />
      <section className="pt-20 pb-12">
        <div className="container-yono">
          <p className="eyebrow mb-6">Contact</p>
          <h1 className="font-heading text-[24px] sm:text-[34px] lg:text-[44px] tracking-tight leading-[1.04] max-w-4xl">
            Présentez-nous votre projet
          </h1>
          <p className="mt-8 text-[15px] sm:text-[16px] leading-[1.85] text-[var(--yono-soft-black)]/80 max-w-3xl">
            Vous êtes camping, PRL, domaine nature, collectivité, exploitant
            touristique ou porteur d'un projet d'hébergement ? Écrivez-nous pour
            échanger sur votre projet, vos emplacements, vos contraintes
            d'exploitation et les modèles les plus adaptés.
          </p>
        </div>
      </section>

      <div className="container-yono"><div className="h-line" /></div>

      <section className="section bg-[var(--yono-beige)]">
        <div className="container-yono grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {done ? (
              <div className="border border-[var(--yono-border)] p-10" data-testid="contact-success">
                <div className="eyebrow mb-3">Merci</div>
                <h2 className="font-heading text-2xl mb-3">
                  Votre demande a bien été envoyée.
                </h2>
                <p className="text-[var(--yono-soft-black)]/75">
                  Nos équipes reviennent vers vous sous 48h ouvrées avec une
                  première proposition adaptée à votre projet.
                </p>
                <button
                  onClick={() => setDone(false)}
                  className="btn-outline mt-6"
                  data-testid="contact-new-btn"
                >
                  Envoyer une autre demande
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="space-y-6"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="eyebrow block mb-2">Nom et prénom *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      required
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-2">Société / Organisation</label>
                    <input
                      type="text"
                      value={form.company}
                      onChange={update("company")}
                      data-testid="input-company"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-2">Email *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-2">Téléphone</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      data-testid="input-phone"
                    />
                  </div>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Type de projet</label>
                  <select
                    value={form.project_type}
                    onChange={update("project_type")}
                    data-testid="select-project-type"
                  >
                    <option value="">Sélectionnez le type de projet</option>
                    {PROJECT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Message *</label>
                  <textarea
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Présentez votre projet, vos emplacements, vos contraintes d'exploitation..."
                    required
                    data-testid="input-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-60"
                  data-testid="submit-contact-btn"
                >
                  {loading ? "Envoi en cours..." : "Envoyer le message"}{" "}
                  <ArrowRight size={16} />
                </button>
                <p className="text-xs text-[var(--yono-warm-grey)]">
                  * Champs obligatoires
                </p>
              </form>
            )}
          </div>

          <aside className="lg:col-span-5 space-y-10">
            <div>
              <p className="eyebrow mb-4">Coordonnées</p>
              <ul className="space-y-4 text-[14px]">
                <li className="flex gap-3 items-start">
                  <Mail size={16} className="mt-1 shrink-0" strokeWidth={1.5} />
                  <span>contact@yono-habitat.com</span>
                </li>
                <li className="flex gap-3 items-start">
                  <Phone size={16} className="mt-1 shrink-0" strokeWidth={1.5} />
                  <span>+33 (0)6 83 58 18 04</span>
                </li>
                <li className="flex gap-3 items-start">
                  <MapPin size={16} className="mt-1 shrink-0" strokeWidth={1.5} />
                  <span>
                    9 rue Pasteur<br />
                    44310 Saint-Philbert-de-Grand-Lieu<br />
                    France
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <p className="eyebrow mb-4">Délais de réponse</p>
              <p className="text-[14px] leading-relaxed text-[var(--yono-soft-black)]/75">
                Nos équipes s'engagent à vous répondre sous 48 heures ouvrées.
                Pour les demandes urgentes, n'hésitez pas à nous appeler
                directement.
              </p>
            </div>

            <div>
              <p className="eyebrow mb-4">Prochaines étapes</p>
              <ol className="space-y-3 text-[14px]">
                {[
                  "Échange téléphonique ou visio pour cadrer votre projet",
                  "Visite de notre atelier si souhaité",
                  "Proposition technique et commerciale adaptée",
                  "Accompagnement tout au long du projet",
                ].map((s, i) => (
                  <li key={s} className="flex gap-4 items-start">
                    <span className="font-mono text-xs text-[var(--yono-warm-grey)] w-6 shrink-0">
                      0{i + 1}
                    </span>
                    <span className="text-[var(--yono-soft-black)]/85">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
